import * as Sentry from "@sentry/capacitor";
import { toast } from "sonner";
import { interfaceTranslator } from "@/i18n/translate";

/** Worded when raised; also the toast's id, so the journal's copy shows once. */
export function lateRollbackMessage(): string {
  return interfaceTranslator("shell.persisted")("lateRollback");
}

/**
 * What a collection's `insert`, `update` or `delete` returns, reduced to the
 * one member this module reads. The `Transaction` those calls return is not a
 * promise: `await collection.update(...)` resumes straight after the
 * optimistic apply and never sees a write that later rolls back.
 */
export interface PendingWrite {
  readonly isPersisted: { readonly promise: Promise<unknown> };
}

/**
 * How long a write may take to confirm before the caller moves on anyway. A
 * local write confirms in well under a second; a change flush the PowerSync
 * adapter cannot complete at all (a SQL error, not an unreadable row) still
 * leaves `isPersisted` unsettled, since the adapter only logs it. Past this,
 * the write is treated as landed and reported, and a rollback that still
 * arrives later is reported too.
 */
export const PERSIST_CONFIRM_TIMEOUT_MS = 10_000;

/**
 * Resolves once `write` is saved in this device's SQLite, and rejects when it
 * was rolled back, so the caller's `catch` sees the failure before it tells the
 * host anything. Saved on the device is all it means: the upload to the server
 * happens later, through PowerSync's queue.
 *
 * Resolves `true` once the write confirmed, and `false` when the wait timed out
 * with the write still unconfirmed: fine for moving on, not for a haptic that
 * tells someone who is not looking that it worked.
 *
 * `label` names the write in the report sent when confirmation times out.
 */
export async function persisted(
  write: PendingWrite,
  label: string
): Promise<boolean> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timedOut = new Promise<"timed-out">((resolve) => {
    timer = setTimeout(() => resolve("timed-out"), PERSIST_CONFIRM_TIMEOUT_MS);
  });
  try {
    const outcome = await Promise.race([write.isPersisted.promise, timedOut]);
    if (outcome === "timed-out") {
      reportUnconfirmedWrite(label);
      // By the time it rolls back the host may be anywhere in the app, so the
      // message cannot name the screen; it can still stop them believing the
      // change stuck.
      warnOnRollback(write, label, lateRollbackMessage());
      return false;
    }
    return true;
  } catch (error) {
    // The caller tells the host; this is the only record a developer gets.
    reportRollback(error, label);
    throw error;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * For bookkeeping whose failure must not reach the host (read watermarks,
 * chat-list previews): nothing waits on the write, and a rollback is reported
 * instead of surfacing as an unhandled rejection.
 */
export function persistInBackground(write: PendingWrite, label: string): void {
  write.isPersisted.promise.catch((error: unknown) => {
    console.warn(`Background write rolled back (${label})`, error);
    reportRollback(error, label);
  });
}

/**
 * For a one-tap action the host has already been told about (a toast with
 * Undo, a haptic): nothing waits, and a rollback is said out loud with
 * `message`, since the screen quietly reverting is easy to miss. Nothing
 * waits on it, so unlike `persisted` it listens for as long as the write takes.
 * `successToast` is the toast that already announced the action: a rollback
 * dismisses it, so the host is not left with "done" and "failed" side by side.
 */
export function warnOnRollback(
  write: PendingWrite,
  label: string,
  message: string,
  successToast?: string | number
): void {
  write.isPersisted.promise.catch((error: unknown) => {
    console.error(`Write rolled back (${label})`, error);
    reportRollback(error, label);
    if (successToast !== undefined) {
      toast.dismiss(successToast);
    }
    // Keyed by the message, so a batch of writes failing together (a
    // multi-row move after a timeout) shows one toast, not one per row.
    toast.error(message, { id: message });
  });
}

/** The developer's record of a write that did not land. */
export function reportRollback(error: unknown, label: string): void {
  Sentry.captureException(error, {
    tags: { area: "collection-write", write: label },
  });
}

function reportUnconfirmedWrite(label: string): void {
  console.warn(
    `Write not confirmed after ${PERSIST_CONFIRM_TIMEOUT_MS} ms (${label})`
  );
  Sentry.captureMessage(`Collection write not confirmed (${label})`, {
    level: "warning",
    tags: { area: "collection-write", write: label },
  });
}
